import { useState, useEffect, useRef } from "react";
import { useSeoMeta } from "@/hooks/useSeoMeta";
import { motion, useInView, AnimatePresence } from "framer-motion";
import { useNavigate, useLocation, Link } from "react-router-dom";
import {
  GraduationCap, BookOpen, PenLine, Brain, Laugh,
  ChevronDown, Sparkles, Presentation, MessageSquare,
  Zap, Users, BookMarked, Menu, X, ArrowRight, ChevronRight,
  Pause, Play, Volume2, VolumeX, Calendar
} from "lucide-react";

/* ─── Character Avatars (SVG) ─── */
const TeacherAvatar = () => (
  <svg width="130" height="140" viewBox="0 0 130 140" className="character-svg">
    <ellipse cx="65" cy="105" rx="38" ry="30" fill="#118AB2" stroke="#073B4C" strokeWidth="3.5" />
    <circle cx="65" cy="52" r="28" fill="#FFF0DB" stroke="#073B4C" strokeWidth="3.5" />
    <ellipse cx="65" cy="27" rx="30" ry="5" fill="#073B4C" />
    <rect x="48" y="16" width="34" height="13" rx="1" fill="#073B4C" />
    <line x1="82" y1="27" x2="88" y2="36" stroke="#FFD166" strokeWidth="2" />
    <circle cx="88" cy="38" r="3" fill="#FFD166" />
    <circle cx="55" cy="50" r="8" fill="none" stroke="#073B4C" strokeWidth="2.5" />
    <circle cx="75" cy="50" r="8" fill="none" stroke="#073B4C" strokeWidth="2.5" />
    <line x1="63" y1="50" x2="67" y2="50" stroke="#073B4C" strokeWidth="2" />
    <circle cx="55" cy="50" r="3" fill="#073B4C" />
    <circle cx="75" cy="50" r="3" fill="#073B4C" />
    <circle cx="56" cy="48" r="1" fill="white" />
    <circle cx="76" cy="48" r="1" fill="white" />
    <path d="M 56 64 Q 65 72 74 64" fill="none" stroke="#073B4C" strokeWidth="2.5" strokeLinecap="round" />
    <path d="M 27 100 Q 32 88 42 92" fill="none" stroke="#073B4C" strokeWidth="3" strokeLinecap="round" />
    <path d="M 103 100 Q 98 88 88 92" fill="none" stroke="#073B4C" strokeWidth="3" strokeLinecap="round" />
  </svg>
);

const StudentAvatar = () => (
  <svg width="130" height="140" viewBox="0 0 130 140" className="character-svg">
    <ellipse cx="65" cy="105" rx="38" ry="30" fill="#FFD166" stroke="#073B4C" strokeWidth="3.5" />
    <circle cx="65" cy="52" r="28" fill="#FFF0DB" stroke="#073B4C" strokeWidth="3.5" />
    <path d="M 40 42 Q 42 20 65 22 Q 88 20 90 42" fill="#073B4C" stroke="#073B4C" strokeWidth="2" />
    <path d="M 48 32 L 52 20" stroke="#073B4C" strokeWidth="2.5" strokeLinecap="round" />
    <path d="M 82 32 L 78 20" stroke="#073B4C" strokeWidth="2.5" strokeLinecap="round" />
    <path d="M 65 28 L 65 16" stroke="#073B4C" strokeWidth="2.5" strokeLinecap="round" />
    <circle cx="55" cy="50" r="5" fill="white" stroke="#073B4C" strokeWidth="2" />
    <circle cx="75" cy="50" r="5" fill="white" stroke="#073B4C" strokeWidth="2" />
    <circle cx="56" cy="49" r="2.5" fill="#073B4C" />
    <circle cx="76" cy="49" r="2.5" fill="#073B4C" />
    <ellipse cx="65" cy="65" rx="5" ry="4" fill="#073B4C" />
    <circle cx="44" cy="58" r="5" fill="#EF476F" opacity="0.25" />
    <circle cx="86" cy="58" r="5" fill="#EF476F" opacity="0.25" />
    <rect x="85" y="88" width="16" height="22" rx="2" fill="#118AB2" stroke="#073B4C" strokeWidth="2" />
    <line x1="88" y1="94" x2="98" y2="94" stroke="white" strokeWidth="1.5" opacity="0.6" />
    <line x1="88" y1="98" x2="96" y2="98" stroke="white" strokeWidth="1.5" opacity="0.6" />
  </svg>
);

const NotesTakerAvatar = () => (
  <svg width="130" height="140" viewBox="0 0 130 140" className="character-svg">
    <ellipse cx="65" cy="105" rx="38" ry="30" fill="#06D6A0" stroke="#073B4C" strokeWidth="3.5" />
    <circle cx="65" cy="52" r="28" fill="#FFF0DB" stroke="#073B4C" strokeWidth="3.5" />
    <path d="M 39 44 Q 40 22 65 25 Q 90 22 91 44" fill="#5D4037" stroke="#073B4C" strokeWidth="2" />
    <line x1="88" y1="32" x2="100" y2="20" stroke="#FFD166" strokeWidth="3.5" strokeLinecap="round" />
    <polygon points="100,20 104,15 97,17" fill="#EF476F" />
    <ellipse cx="55" cy="50" rx="4" ry="3.5" fill="#073B4C" />
    <ellipse cx="75" cy="50" rx="4" ry="3.5" fill="#073B4C" />
    <circle cx="56" cy="49" r="1" fill="white" />
    <circle cx="76" cy="49" r="1" fill="white" />
    <line x1="49" y1="42" x2="61" y2="43.5" stroke="#073B4C" strokeWidth="2.5" strokeLinecap="round" />
    <line x1="81" y1="43.5" x2="69" y2="42" stroke="#073B4C" strokeWidth="2.5" strokeLinecap="round" />
    <path d="M 57 63 L 73 63" stroke="#073B4C" strokeWidth="2.5" strokeLinecap="round" />
    <rect x="18" y="85" width="20" height="26" rx="2" fill="white" stroke="#073B4C" strokeWidth="2.5" />
    <line x1="22" y1="91" x2="34" y2="91" stroke="#06D6A0" strokeWidth="1.5" />
    <line x1="22" y1="96" x2="34" y2="96" stroke="#06D6A0" strokeWidth="1.5" />
    <line x1="22" y1="101" x2="30" y2="101" stroke="#06D6A0" strokeWidth="1.5" />
  </svg>
);

const DeepThinkerAvatar = () => (
  <svg width="130" height="140" viewBox="0 0 130 140" className="character-svg">
    <ellipse cx="65" cy="105" rx="38" ry="30" fill="#8338EC" stroke="#073B4C" strokeWidth="3.5" />
    <circle cx="65" cy="52" r="28" fill="#FFF0DB" stroke="#073B4C" strokeWidth="3.5" />
    <path d="M 39 48 Q 37 22 58 18 Q 78 15 86 24 Q 92 32 91 48" fill="#2D1B69" stroke="#073B4C" strokeWidth="2" />
    <circle cx="96" cy="28" r="4" fill="white" stroke="#073B4C" strokeWidth="1.5" />
    <circle cx="105" cy="18" r="6" fill="white" stroke="#073B4C" strokeWidth="1.5" />
    <circle cx="115" cy="8" r="8" fill="white" stroke="#073B4C" strokeWidth="1.5" />
    <path d="M 112 5 Q 115 2 118 5 Q 120 8 117 10" fill="none" stroke="#8338EC" strokeWidth="1.5" />
    <circle cx="55" cy="50" r="4.5" fill="white" stroke="#073B4C" strokeWidth="2" />
    <circle cx="75" cy="50" r="4.5" fill="white" stroke="#073B4C" strokeWidth="2" />
    <circle cx="56" cy="48" r="2" fill="#073B4C" />
    <circle cx="76" cy="48" r="2" fill="#073B4C" />
    <path d="M 58 64 Q 65 67 72 64" fill="none" stroke="#073B4C" strokeWidth="2" strokeLinecap="round" />
    <path d="M 80 68 Q 82 72 80 82" fill="none" stroke="#FFF0DB" strokeWidth="6" />
    <path d="M 80 68 Q 82 72 80 82" fill="none" stroke="#073B4C" strokeWidth="2.5" strokeLinecap="round" />
  </svg>
);

const FunnyMateAvatar = () => (
  <svg width="130" height="140" viewBox="0 0 130 140" className="character-svg">
    <ellipse cx="65" cy="105" rx="38" ry="30" fill="#EF476F" stroke="#073B4C" strokeWidth="3.5" />
    <circle cx="65" cy="52" r="28" fill="#FFF0DB" stroke="#073B4C" strokeWidth="3.5" />
    <path d="M 44 38 L 47 18 L 54 32 L 60 14 L 65 30 L 70 12 L 76 32 L 83 18 L 86 38" fill="#FF6B35" stroke="#073B4C" strokeWidth="2" />
    <polygon points="100,34 102,28 104,34 99,31 105,31" fill="#FFD166" stroke="#073B4C" strokeWidth="1" />
    <polygon points="28,30 30,24 32,30 27,27 33,27" fill="#FFD166" stroke="#073B4C" strokeWidth="1" />
    <path d="M 49 48 Q 54 43 59 48" fill="none" stroke="#073B4C" strokeWidth="2.5" strokeLinecap="round" />
    <path d="M 71 48 Q 76 43 81 48" fill="none" stroke="#073B4C" strokeWidth="2.5" strokeLinecap="round" />
    <path d="M 52 62 Q 65 76 78 62" fill="white" stroke="#073B4C" strokeWidth="2.5" strokeLinecap="round" />
    <line x1="58" y1="66" x2="72" y2="66" stroke="#073B4C" strokeWidth="1" />
    <circle cx="44" cy="56" r="5" fill="#EF476F" opacity="0.35" />
    <circle cx="86" cy="56" r="5" fill="#EF476F" opacity="0.35" />
    <path d="M 103 96 Q 100 84 92 82 Q 96 74 102 68" fill="none" stroke="#073B4C" strokeWidth="3" strokeLinecap="round" />
  </svg>
);

const CuriousMindAvatar = () => (
  <svg width="130" height="140" viewBox="0 0 130 140" className="character-svg">
    <ellipse cx="65" cy="105" rx="38" ry="30" fill="#FF6B35" stroke="#073B4C" strokeWidth="3.5" />
    <circle cx="65" cy="52" r="28" fill="#FFF0DB" stroke="#073B4C" strokeWidth="3.5" />
    <path d="M 42 38 Q 44 18 65 16 Q 86 18 88 38" fill="#D35400" stroke="#073B4C" strokeWidth="2" />
    <path d="M 58 18 Q 56 10 62 8" stroke="#D35400" strokeWidth="2.5" strokeLinecap="round" fill="none" />
    <path d="M 72 18 Q 74 10 68 8" stroke="#D35400" strokeWidth="2.5" strokeLinecap="round" fill="none" />
    <circle cx="53" cy="48" r="6" fill="white" stroke="#073B4C" strokeWidth="2" />
    <circle cx="77" cy="48" r="6" fill="white" stroke="#073B4C" strokeWidth="2" />
    <circle cx="54" cy="46" r="2.5" fill="#073B4C" />
    <circle cx="78" cy="46" r="2.5" fill="#073B4C" />
    <circle cx="55" cy="45" r="1" fill="white" />
    <circle cx="79" cy="45" r="1" fill="white" />
    <line x1="47" y1="40" x2="59" y2="42" stroke="#073B4C" strokeWidth="2" strokeLinecap="round" />
    <line x1="83" y1="40" x2="71" y2="42" stroke="#073B4C" strokeWidth="2" strokeLinecap="round" />
    <circle cx="65" cy="62" r="3.5" fill="#073B4C" />
    <text x="96" y="22" fontFamily="Fredoka, sans-serif" fontSize="22" fontWeight="bold" fill="#FF6B35" stroke="#073B4C" strokeWidth="1.5">?</text>
    <text x="18" y="30" fontFamily="Fredoka, sans-serif" fontSize="16" fontWeight="bold" fill="#FFD166" stroke="#073B4C" strokeWidth="1">!</text>
    <path d="M 25 98 Q 28 85 38 88" fill="none" stroke="#073B4C" strokeWidth="3" strokeLinecap="round" />
    <path d="M 105 98 Q 102 85 92 88" fill="none" stroke="#073B4C" strokeWidth="3" strokeLinecap="round" />
  </svg>
);

const AVATAR_MAP = {
  teacher: TeacherAvatar,
  student: StudentAvatar,
  notestaker: NotesTakerAvatar,
  deepthinker: DeepThinkerAvatar,
  funnymate: FunnyMateAvatar,
  curiousmind: CuriousMindAvatar,
};

const CHARACTERS = [
  { key: "teacher", name: "Professor Sage", role: "The Teacher", color: "#118AB2", bg: "#D4F0FF", desc: "Guides your learning journey with expert knowledge and engaging narration" },
  { key: "student", name: "You", role: "The Student", color: "#FFD166", bg: "#FFF8E1", desc: "Ask anything, explore any topic, and learn at your own pace" },
  { key: "notestaker", name: "Noti", role: "Notes Taker", color: "#06D6A0", bg: "#E0FFF4", desc: "Captures every important detail so you never miss a thing" },
  { key: "deepthinker", name: "Deepa", role: "Deep Thinker", color: "#8338EC", bg: "#F0E6FF", desc: "Asks the tough questions and digs deeper into every topic" },
  { key: "funnymate", name: "Chuckle", role: "Funny Mate", color: "#EF476F", bg: "#FFE4EC", desc: "Makes learning fun with humor, memes, and witty commentary" },
  { key: "curiousmind", name: "Curio", role: "Curious Mind", color: "#FF6B35", bg: "#FFF0E0", desc: "Always curious, loves asking why and how about everything" },
];

const STEPS = [
  { icon: MessageSquare, title: "Ask", desc: "Type any topic you want to learn about in a simple prompt", color: "#FFD166", num: "01" },
  { icon: Sparkles, title: "Generate", desc: "AI creates personalized slides, narration, and your AI classmates", color: "#06D6A0", num: "02" },
  { icon: Presentation, title: "Learn", desc: "Enter your interactive classroom and start an engaging learning session", color: "#EF476F", num: "03" },
];

const FEATURES = [
  { icon: Sparkles, title: "AI Course Generation", desc: "Get a full course with slides and narration generated instantly from any topic", color: "#FFD166" },
  { icon: Users, title: "AI Classmates", desc: "Learn alongside smart AI characters that take notes, think deep, and crack jokes", color: "#8338EC" },
  { icon: Presentation, title: "Interactive Slides", desc: "Beautiful slides with narration, visuals, and interactive elements", color: "#118AB2" },
  { icon: Zap, title: "Instant Learning", desc: "No waiting. Type a topic and enter your personalized classroom in seconds", color: "#EF476F" },
  { icon: BookMarked, title: "Any Subject", desc: "From Python to Philosophy, quantum physics to cooking - learn anything", color: "#06D6A0" },
  { icon: BookOpen, title: "Personal Pace", desc: "Go fast or slow. Replay, revisit, and learn on your own schedule", color: "#FF6B35" },
];

/* ─── Animated Section Wrapper ─── */
const AnimSection = ({ children, className, id }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });
  return (
    <motion.section
      ref={ref}
      id={id}
      initial={{ opacity: 0 }}
      animate={isInView ? { opacity: 1 } : {}}
      transition={{ duration: 0.6 }}
      className={className}
    >
      {children}
    </motion.section>
  );
};

/* ─── Character Card ─── */
const CharacterCard = ({ character, index }) => {
  const Avatar = AVATAR_MAP[character.key];
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.1, duration: 0.5 }}
      whileHover={{ y: -8, transition: { type: "spring", stiffness: 300, damping: 20 } }}
      className="neo-card bg-white border-4 border-[#073B4C] rounded-3xl p-5 shadow-[6px_6px_0px_#073B4C] flex flex-col items-center text-center"
    >
      <div className="bobble mb-3" style={{ animationDelay: `${index * 0.4}s` }}>
        <Avatar />
      </div>
      <h3 className="font-heading text-lg font-bold text-[#073B4C]">{character.name}</h3>
      <span
        className="inline-block mt-1 mb-2 text-xs font-bold px-3 py-1 rounded-full border-2 border-[#073B4C] text-white shadow-[2px_2px_0px_#073B4C]"
        style={{ backgroundColor: character.color }}
      >
        {character.role}
      </span>
      <p className="text-sm text-[#495057] leading-snug">{character.desc}</p>
    </motion.div>
  );
};

/* ─── NCERT Mega-Dropdown ─── */
const NCERT_MENU = [
  {
    board: "CBSE / NCERT",
    classes: [
      {
        label: "Class 10",
        subjects: [
          { label: "Mathematics", href: "/learn/ncert-class-10-mathematics", color: "#FFD166" },
          { label: "Science", href: "/learn/ncert-class-10-science", color: "#06D6A0" },
        ],
      },
    ],
  },
];

function NcertDropdown() {
  const [open, setOpen] = useState(false);
  const [activeClass, setActiveClass] = useState(NCERT_MENU[0].classes[0].label);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const currentClass = NCERT_MENU[0].classes.find(c => c.label === activeClass);

  return (
    <div ref={ref} className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button
        className="flex items-center gap-1 font-body font-semibold text-[#073B4C] hover:text-[#118AB2] transition-colors py-1"
        onClick={() => setOpen(v => !v)}
        aria-haspopup="true"
        aria-expanded={open}
      >
        CBSE / NCERT <ChevronDown size={15} className={`transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.18 }}
            className="absolute left-1/2 -translate-x-1/2 top-full mt-2 w-64 bg-white border-3 border-[#073B4C] rounded-2xl shadow-[5px_5px_0px_#073B4C] overflow-hidden z-50"
          >
            <div className="bg-[#F0F4F8] border-b-2 border-[#073B4C] px-3 py-2 flex gap-2">
              {NCERT_MENU[0].classes.map(cls => (
                <button
                  key={cls.label}
                  onMouseEnter={() => setActiveClass(cls.label)}
                  onClick={() => setActiveClass(cls.label)}
                  className={`flex-1 flex items-center justify-between px-3 py-1.5 rounded-xl text-sm font-bold border-2 transition-all ${activeClass === cls.label
                      ? "bg-[#FFD166] border-[#073B4C] text-[#073B4C] shadow-[2px_2px_0px_#073B4C]"
                      : "bg-white border-transparent text-[#073B4C]/60 hover:border-[#073B4C]/30"
                    }`}
                >
                  {cls.label} <ChevronRight size={12} />
                </button>
              ))}
            </div>
            <div className="p-3 flex flex-col gap-1.5">
              {currentClass?.subjects.map(sub => (
                <Link
                  key={sub.label}
                  to={sub.href}
                  onClick={() => setOpen(false)}
                  className="flex items-center justify-between px-4 py-3 rounded-xl border-2 border-[#073B4C] font-semibold text-sm text-[#073B4C] hover:shadow-[3px_3px_0px_#073B4C] hover:translate-y-[-1px] transition-all no-underline"
                  style={{ backgroundColor: sub.color }}
                >
                  {sub.label}
                  <ArrowRight size={14} />
                </Link>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ─── Product Demo Video ─── */
const SlateDemoVideo = () => {
  const videoRef = useRef(null);
  const wrapRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const el = wrapRef.current;
    const video = videoRef.current;
    if (!el || !video) return;

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio > 0.5) {
            video.play().then(() => setIsPlaying(true)).catch(() => { });
          } else {
            video.pause();
            setIsPlaying(false);
          }
        });
      },
      { threshold: [0, 0.5, 1] }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      v.play().then(() => setIsPlaying(true)).catch(() => { });
    } else {
      v.pause();
      setIsPlaying(false);
    }
  };

  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setIsMuted(v.muted);
  };

  return (
    <div
      ref={wrapRef}
      className="relative w-full max-w-[960px] mx-auto bg-white border-4 border-[#073B4C] rounded-[22px] shadow-[8px_8px_0px_#073B4C] overflow-hidden"
    >
      {/* Browser chrome */}
      <div className="flex items-center gap-3 px-4 py-2.5 bg-[#F0F4F8] border-b-[3px] border-[#073B4C]">
        <div className="flex gap-1.5">
          <span className="w-3 h-3 rounded-full border-2 border-[#073B4C] bg-[#EF476F]" />
          <span className="w-3 h-3 rounded-full border-2 border-[#073B4C] bg-[#FFD166]" />
          <span className="w-3 h-3 rounded-full border-2 border-[#073B4C] bg-[#06D6A0]" />
        </div>
        <div className="flex-1 text-center text-xs font-bold text-[#495057] bg-white border-2 border-[#073B4C] rounded-full px-3 py-0.5">
          app.slateup.ai · live classroom
        </div>
      </div>

      {/* Video */}
      <div className="relative bg-black aspect-video">
        {!loaded && (
          <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-[#118AB2] via-[#8338EC] to-[#EF476F]">
            <div className="flex flex-col items-center gap-3 text-white/90">
              <Sparkles size={36} className="animate-pulse" />
              <span className="font-heading text-lg font-semibold tracking-wide">Loading demo…</span>
            </div>
          </div>
        )}
        <video
          ref={videoRef}
          src="/slate-demo.mp4"
          muted
          playsInline
          loop
          preload="metadata"
          onLoadedData={() => setLoaded(true)}
          className="w-full h-full object-cover"
          data-testid="demo-video"
        />

        <div className="absolute bottom-4 right-4 flex items-center gap-2">
          <button
            type="button"
            onClick={togglePlay}
            aria-label={isPlaying ? "Pause video" : "Play video"}
            className="w-11 h-11 rounded-full bg-white text-[#073B4C] border-2 border-[#073B4C] shadow-[3px_3px_0px_#073B4C] flex items-center justify-center hover:scale-110 active:translate-y-[1px] active:shadow-[1px_1px_0px_#073B4C] transition-all"
          >
            {isPlaying ? <Pause size={18} strokeWidth={2.5} /> : <Play size={18} strokeWidth={2.5} className="ml-0.5" />}
          </button>
          <button
            type="button"
            onClick={toggleMute}
            aria-label={isMuted ? "Unmute video" : "Mute video"}
            className="h-11 rounded-full bg-[#FFD166] text-[#073B4C] border-2 border-[#073B4C] shadow-[3px_3px_0px_#073B4C] px-4 flex items-center gap-2 font-bold text-sm hover:scale-105 active:translate-y-[1px] active:shadow-[1px_1px_0px_#073B4C] transition-all"
          >
            {isMuted ? <VolumeX size={18} strokeWidth={2.5} /> : <Volume2 size={18} strokeWidth={2.5} />}
            <span className="hidden sm:inline">{isMuted ? "Unmute" : "Mute"}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════
   MAIN LANDING PAGE
   ═══════════════════════════════════════════════════ */
export default function LandingPage() {
  useSeoMeta({
    title: "Slate - AI-powered Interactive Classroom",
    description: "Slate is an AI-powered interactive classroom where you learn with AI classmates - not just a chatbot. Personalised explanations, instant doubt resolution, and adaptive courses for NCERT, JEE, NEET and beyond.",
    canonical: "https://www.slateup.ai/",
  });

  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const containerRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const onScroll = () => setScrolled(container.scrollTop > 50);
    container.addEventListener("scroll", onScroll);
    return () => container.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const PATH_TO_SECTION = {
      "/demo": "demo",
      "/characters": "characters",
      "/how-it-works": "how-it-works",
      "/features": "features",
    };
    const sectionId = PATH_TO_SECTION[location.pathname];
    if (sectionId) {
      setTimeout(() => {
        document.getElementById(sectionId)?.scrollIntoView({ behavior: "smooth" });
      }, 300);
    }
  }, [location.pathname]);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    setMenuOpen(false);
  };

  const openCalendly = () => {
    window.open("https://calendly.com/hello-slateup/30min", "_blank", "noopener,noreferrer");
  };

  const appSchema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Slate",
    "url": "https://slateup.ai",
    "applicationCategory": "EducationApplication",
    "operatingSystem": "Web",
    "offers": [
      {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "USD",
        "description": "Free starter plan - 2 classrooms forever",
      },
      {
        "@type": "Offer",
        "price": "19",
        "priceCurrency": "USD",
        "description": "Pro plan - 30 classrooms per month",
      },
    ],
    "description": "AI-powered interactive classroom where you learn with AI classmates. Personalised courses, instant doubt resolution, and adaptive lessons for NCERT, JEE, NEET and beyond.",
    "screenshot": "https://www.slateup.ai/og-image.png",
    "publisher": {
      "@type": "Organization",
      "name": "Slate",
      "url": "https://slateup.ai",
    },
  };

  return (
    <div className="h-screen flex flex-col">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(appSchema) }}
      />
      {/* ── Navbar ── */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? "glass-nav shadow-sm" : "bg-transparent"}`}
        data-testid="navbar"
      >
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-16">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => { navigate("/"); containerRef.current?.scrollTo({ top: 0, behavior: "smooth" }); }}>
            <span className="font-heading text-2xl font-bold text-[#073B4C] tracking-[-0.025em]">SLATE UP</span>
          </div>

          <nav className="hidden md:flex items-center gap-8">
            <NcertDropdown />
            <Link
              to="/pricing"
              className="font-body font-semibold text-[#073B4C] hover:text-[#118AB2] transition-colors no-underline"
            >
              Pricing
            </Link>
            <button
              onClick={() => scrollTo("demo")}
              className="font-body font-semibold text-[#073B4C] hover:text-[#118AB2] transition-colors"
            >
              Demo
            </button>
            <Link
              to="/contact"
              className="font-body font-semibold text-[#073B4C] hover:text-[#118AB2] transition-colors no-underline"
            >
              Contact
            </Link>
          </nav>

          <div className="flex items-center gap-3">
            <a
              href="https://app.slateup.ai/auth/login"
              target="_blank"
              rel="noopener noreferrer"
              className="hidden md:inline-flex btn-press bg-[#073B4C] text-white rounded-full px-5 py-2 font-bold text-sm border-2 border-[#073B4C] shadow-[3px_3px_0px_#073B4C] hover:scale-105 transition-transform no-underline"
              data-testid="nav-try-now"
            >
              Try Now
            </a>
            <button className="md:hidden text-[#073B4C]" onClick={() => setMenuOpen(!menuOpen)} data-testid="mobile-menu-toggle">
              {menuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {menuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="md:hidden bg-white border-t-2 border-[#073B4C] px-6 py-4 flex flex-col gap-3"
          >
            <button
              type="button"
              onClick={() => { navigate("/blogs"); setMenuOpen(false); }}
              className="font-body font-semibold text-[#073B4C] text-left py-2 hover:text-[#118AB2]"
            >
              Blogs
            </button>
            <button
              type="button"
              onClick={() => { navigate("/pricing"); setMenuOpen(false); }}
              className="font-body font-semibold text-[#073B4C] text-left py-2 hover:text-[#118AB2]"
            >
              Pricing
            </button>
            <button
              type="button"
              onClick={() => scrollTo("demo")}
              className="font-body font-semibold text-[#073B4C] text-left py-2 hover:text-[#118AB2]"
            >
              Demo
            </button>
            <button
              type="button"
              onClick={() => { navigate("/contact"); setMenuOpen(false); }}
              className="font-body font-semibold text-[#073B4C] text-left py-2 hover:text-[#118AB2]"
            >
              Contact
            </button>
            <a
              href="https://app.slateup.ai/auth/login"
              target="_blank"
              rel="noopener noreferrer"
              className="font-body font-semibold text-[#EF476F] text-left py-2 no-underline"
            >
              Try Now →
            </a>
            {/* CBSE / NCERT mobile section */}
            <div className="border-t border-[#073B4C]/10 pt-3">
              <p className="font-body text-xs font-bold uppercase tracking-wider text-[#073B4C]/40 mb-2">CBSE / NCERT</p>
              <Link
                to="/learn/ncert-class-10-mathematics"
                onClick={() => setMenuOpen(false)}
                className="flex items-center justify-between py-2.5 px-4 rounded-xl border-2 border-[#073B4C] bg-[#FFD166] font-semibold text-sm text-[#073B4C] shadow-[2px_2px_0px_#073B4C] no-underline"
              >
                <span>Class 10 · Mathematics</span>
                <ArrowRight size={14} />
              </Link>
              <Link
                to="/learn/ncert-class-10-science"
                onClick={() => setMenuOpen(false)}
                className="flex items-center justify-between py-2.5 px-4 rounded-xl border-2 border-[#073B4C] bg-[#06D6A0] font-semibold text-sm text-[#073B4C] shadow-[2px_2px_0px_#073B4C] no-underline mt-1.5"
              >
                <span>Class 10 · Science</span>
                <ArrowRight size={14} />
              </Link>
            </div>
          </motion.div>
        )}
      </header>

      {/* ── Main Scroll Container ── */}
      <div ref={containerRef} className="snap-container pb-[4.75rem] md:pb-0" data-testid="scroll-container">

        {/* ═══ HERO ═══ */}
        <section className="snap-section flex flex-col items-center justify-center bg-[#FDFDFD] overflow-hidden px-6 pt-16" id="hero" data-testid="hero-section">
          {/* Floating shapes */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            <div className="floating-shape absolute top-[8%] left-[4%] w-16 h-16 md:w-24 md:h-24 bg-[#FFD166] rounded-full border-4 border-[#073B4C] opacity-50" style={{ animationDelay: "0s" }} />
            <div className="floating-shape-reverse absolute top-[15%] right-[8%] w-14 h-14 md:w-20 md:h-20 bg-[#EF476F] rounded-2xl border-4 border-[#073B4C] opacity-50" style={{ animationDelay: "1s" }} />
            <div className="floating-shape absolute bottom-[12%] left-[12%] w-20 h-20 md:w-28 md:h-28 bg-[#118AB2] rounded-full border-4 border-[#073B4C] opacity-30" style={{ animationDelay: "2s" }} />
            <div className="floating-shape-reverse absolute bottom-[20%] right-[6%] w-12 h-12 md:w-16 md:h-16 bg-[#06D6A0] rounded-3xl border-4 border-[#073B4C] opacity-40" style={{ animationDelay: "0.5s" }} />
            <div className="floating-shape absolute top-[45%] left-[50%] w-10 h-10 bg-[#8338EC] rounded-full border-4 border-[#073B4C] opacity-25" style={{ animationDelay: "1.5s" }} />
            <div className="floating-shape-reverse absolute top-[60%] left-[25%] w-8 h-8 bg-[#FF6B35] rounded-lg border-3 border-[#073B4C] opacity-30" style={{ animationDelay: "3s" }} />
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="relative z-10 text-center max-w-4xl"
          >
            <div className="flex items-center justify-center gap-3 mb-3">
              <h1 className="font-heading text-6xl md:text-8xl lg:text-9xl font-bold tracking-tighter text-[#073B4C]" data-testid="hero-title">
                SLATE UP
              </h1>
            </div>
            <p className="font-body text-base md:text-lg text-[#495057] max-w-2xl mx-auto mb-8 leading-relaxed">
              Your AI-powered interactive classroom. Ask for any topic and get a personalized course
              with slides, narration, and AI classmates in seconds.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="https://app.slateup.ai/auth/login"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-press inline-flex items-center gap-2 bg-[#073B4C] text-white rounded-full px-8 py-4 font-bold text-lg border-2 border-[#073B4C] shadow-[4px_4px_0px_#073B4C] hover:scale-105 transition-transform no-underline"
                data-testid="hero-try-now"
              >
                Try Now <ArrowRight size={20} />
              </a>
              <button
                onClick={openCalendly}
                className="btn-press inline-flex items-center gap-2 bg-white text-[#073B4C] rounded-full px-8 py-4 font-bold text-lg border-2 border-[#073B4C] shadow-[4px_4px_0px_#073B4C] hover:scale-105 transition-transform"
              >
                <Calendar size={20} /> Schedule a Demo
              </button>
            </div>

            <button onClick={() => scrollTo("demo")} className="mt-10 inline-block" data-testid="scroll-down">
              <ChevronDown className="mx-auto animate-bounce text-[#073B4C]" size={32} />
            </button>
          </motion.div>
        </section>

        {/* ═══ PRODUCT DEMO ═══ */}
        <AnimSection id="demo" className="snap-section flex flex-col items-center justify-center bg-white px-6 py-20 relative overflow-hidden" data-testid="demo-section">
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            <div className="floating-shape absolute top-[10%] left-[6%] w-16 h-16 bg-[#FFD166] rounded-2xl border-4 border-[#073B4C] opacity-40" />
            <div className="floating-shape-reverse absolute bottom-[14%] right-[6%] w-20 h-20 bg-[#06D6A0] rounded-full border-4 border-[#073B4C] opacity-40" />
            <div className="floating-shape absolute top-[20%] right-[12%] w-10 h-10 bg-[#8338EC] rounded-lg border-4 border-[#073B4C] opacity-30" />
          </div>

          <div className="relative z-10 flex flex-col items-center w-full">
            <span className="text-[11.5px] font-extrabold tracking-[0.12em] uppercase text-[#118AB2] mb-2">See it in action</span>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-[#073B4C] text-center leading-[1.05] mb-3"
            >
              Your classroom, <em className="not-italic text-[#EF476F]">on demand</em>.
            </motion.h2>
            <p className="font-body text-base md:text-lg text-[#495057] text-center max-w-xl mb-10 leading-relaxed">
              Ask for any topic, and SLATE UP builds a live, interactive lesson - slides, narration and AI classmates included.
            </p>

            <SlateDemoVideo />

            <div className="flex flex-wrap justify-center gap-3 mt-8">
              {[
                { c: "#EF476F", v: "< 30s", l: "to generate" },
                { c: "#FFD166", v: "6", l: "AI classmates" },
                { c: "#06D6A0", v: "∞", l: "topics covered" },
              ].map((s) => (
                <div key={s.l} className="flex items-center gap-2.5 bg-white border-2 border-[#073B4C] rounded-full px-4 py-2 shadow-[3px_3px_0px_#073B4C]">
                  <span className="w-2.5 h-2.5 rounded-full border-2 border-[#073B4C]" style={{ backgroundColor: s.c }} />
                  <span className="font-heading text-xl font-bold text-[#073B4C] leading-none">{s.v}</span>
                  <span className="text-[11.5px] font-bold text-[#495057] leading-none">{s.l}</span>
                </div>
              ))}
            </div>
          </div>
        </AnimSection>

        {/* ═══ CHARACTERS ═══ */}
        <AnimSection id="characters" className="snap-section flex flex-col items-center justify-center bg-[#FFD166] px-6 py-20" data-testid="characters-section">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-[#073B4C] text-center mb-3"
          >
            Meet Your AI Classmates
          </motion.h2>
          <p className="font-body text-base md:text-lg text-[#073B4C] text-center max-w-2xl mb-10 opacity-75">
            Every SLATE UP classroom comes with a unique cast of AI characters that make learning collaborative and fun.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-5 max-w-7xl w-full">
            {CHARACTERS.map((char, i) => (
              <CharacterCard key={char.key} character={char} index={i} />
            ))}
          </div>
        </AnimSection>

        {/* ═══ HOW IT WORKS ═══ */}
        <AnimSection id="how-it-works" className="snap-section flex flex-col items-center justify-center bg-[#118AB2] px-6 py-20" data-testid="how-it-works-section">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white text-center mb-16"
          >
            How It Works
          </motion.h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl w-full">
            {STEPS.map((step, i) => (
              <motion.div
                key={step.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.15, duration: 0.5 }}
                className="neo-card bg-white border-4 border-[#073B4C] rounded-3xl p-8 shadow-[6px_6px_0px_#073B4C] text-center"
              >
                <span className="font-heading text-5xl font-bold" style={{ color: step.color }}>{step.num}</span>
                <div className="flex justify-center my-4">
                  <div className="w-16 h-16 rounded-2xl border-3 border-[#073B4C] flex items-center justify-center" style={{ backgroundColor: step.color }}>
                    <step.icon size={32} color="#073B4C" strokeWidth={2.5} />
                  </div>
                </div>
                <h3 className="font-heading text-2xl font-bold text-[#073B4C] mb-2">{step.title}</h3>
                <p className="font-body text-[#495057]">{step.desc}</p>
              </motion.div>
            ))}
          </div>
        </AnimSection>

        {/* ═══ FEATURES ═══ */}
        <AnimSection id="features" className="snap-section flex flex-col items-center justify-center bg-[#FDFDFD] px-6 py-20" data-testid="features-section">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-[#073B4C] text-center mb-4"
          >
            What Makes SLATE UP Special
          </motion.h2>
          <p className="font-body text-base md:text-lg text-[#495057] text-center max-w-2xl mb-12">
            A new way to learn - powered by AI, designed for you.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl w-full">
            {FEATURES.map((feat, i) => (
              <motion.div
                key={feat.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08, duration: 0.4 }}
                whileHover={{ y: -6, transition: { type: "spring", stiffness: 300, damping: 20 } }}
                className="neo-card bg-white border-4 border-[#073B4C] rounded-3xl p-6 shadow-[6px_6px_0px_#073B4C]"
              >
                <div
                  className="w-14 h-14 rounded-2xl border-3 border-[#073B4C] flex items-center justify-center mb-4"
                  style={{ backgroundColor: feat.color }}
                >
                  <feat.icon size={28} color="#073B4C" strokeWidth={2.5} />
                </div>
                <h3 className="font-heading text-xl font-bold text-[#073B4C] mb-2">{feat.title}</h3>
                <p className="font-body text-sm text-[#495057] leading-relaxed">{feat.desc}</p>
              </motion.div>
            ))}
          </div>
        </AnimSection>

        {/* ═══ SCHEDULE A DEMO CTA + FOOTER ═══ */}
        <section id="cta" className="snap-section flex flex-col" data-testid="cta-section">
          <div className="flex-1 flex flex-col items-center justify-center bg-[#EF476F] px-6 py-20">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="text-center max-w-2xl"
            >
              <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4">
                Schedule a Demo
              </h2>
              <p className="font-body text-lg text-white/85 mb-8 leading-relaxed">
                See SLATE UP in action with a personalised walkthrough. Our team will show you
                how AI-powered learning can transform education for your students.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <button
                  onClick={openCalendly}
                  className="btn-press inline-flex items-center gap-2 bg-[#FFD166] text-[#073B4C] rounded-full px-8 py-4 font-bold text-lg border-2 border-[#073B4C] shadow-[4px_4px_0px_#073B4C] hover:scale-105 transition-transform"
                  data-testid="schedule-demo-button"
                >
                  <Calendar size={22} /> Schedule a Demo
                </button>
                <a
                  href="https://app.slateup.ai/auth/login"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-press inline-flex items-center gap-2 bg-white text-[#073B4C] rounded-full px-8 py-4 font-bold text-lg border-2 border-[#073B4C] shadow-[4px_4px_0px_#073B4C] hover:scale-105 transition-transform no-underline"
                  data-testid="enter-slate-button"
                >
                  Try SLATE UP <ArrowRight size={20} />
                </a>
              </div>
            </motion.div>
          </div>

          {/* Footer */}
          <footer className="bg-[#073B4C] px-6 py-12" data-testid="footer">
            <div className="max-w-6xl mx-auto">
              <div className="flex flex-col md:flex-row items-center md:items-start justify-between gap-8">
                <div>
                  <span className="font-heading text-3xl font-bold text-white tracking-[-0.025em]">SLATE UP</span>
                  <p className="font-body text-white/40 text-sm mt-3 max-w-xs">
                    AI-powered interactive classroom. Learn anything, with anyone, anytime.
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row gap-8 text-center sm:text-left">
                  <div>
                    <h4 className="font-heading text-sm font-bold text-[#FFD166] uppercase tracking-wider mb-3">Product</h4>
                    <div className="flex flex-col gap-2">
                      <Link to="/blogs" className="font-body text-white/60 hover:text-white text-sm transition-colors text-left no-underline">
                        Blogs
                      </Link>
                      <Link to="/pricing" className="font-body text-white/60 hover:text-white text-sm transition-colors text-left no-underline">
                        Pricing
                      </Link>
                      <button onClick={() => scrollTo("features")} className="font-body text-white/60 hover:text-white text-sm transition-colors text-left">Features</button>
                      <button onClick={() => scrollTo("how-it-works")} className="font-body text-white/60 hover:text-white text-sm transition-colors text-left">How It Works</button>
                    </div>
                  </div>
                  <div>
                    <h4 className="font-heading text-sm font-bold text-[#FFD166] uppercase tracking-wider mb-3">Company</h4>
                    <div className="flex flex-col gap-2">
                      <a href="https://app.slateup.ai" target="_blank" rel="noopener noreferrer" className="font-body text-white/60 hover:text-white text-sm transition-colors no-underline">SLATE UP App</a>
                      <Link to="/contact" className="font-body text-white/60 hover:text-white text-sm transition-colors no-underline">Contact</Link>
                      <span className="font-body text-white/60 text-sm">hello@slateup.ai</span>
                    </div>
                  </div>
                  <div>
                    <h4 className="font-heading text-sm font-bold text-[#FFD166] uppercase tracking-wider mb-3">Legal</h4>
                    <div className="flex flex-col gap-2">
                      <Link to="/terms" className="font-body text-white/60 hover:text-white text-sm transition-colors no-underline">Terms &amp; Conditions</Link>
                      <Link to="/privacy" className="font-body text-white/60 hover:text-white text-sm transition-colors no-underline">Privacy Policy</Link>
                    </div>
                  </div>
                </div>
              </div>
              <div className="border-t border-white/10 mt-8 pt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
                <p className="font-body text-white/40 text-sm">&copy; {new Date().getFullYear()} Slate Up. All rights reserved.</p>
                <a href="https://www.toolpilot.ai" target="_blank" rel="noopener noreferrer">
                  <img src="https://www.toolpilot.ai/cdn/shop/files/f-w_300x66_crop_center.png?v=1695883028" alt="Featured on ToolPilot" width="150" height="33" style={{ opacity: 0.7 }} />
                </a>
              </div>
            </div>
          </footer>
        </section>
      </div>
    </div>
  );
}
